import React, { Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Cargado solo en esta página — evita meter Three.js en el bundle de todas las demás
const StarsBackground = lazy(() => import('../components/Three/StarsBackground'));

const NotFoundPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
      style={{
        position: 'relative',
        overflow: 'hidden',
        padding: '10rem 2rem 6rem',
        minHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        background: '#000',
      }}
    >
      <Suspense fallback={null}>
        <StarsBackground />
      </Suspense>

      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      {/* Big 404 */}
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{
          fontSize: 'clamp(6rem, 15vw, 12rem)',
          fontWeight: 900,
          fontFamily: 'var(--font-heading)',
          lineHeight: 1,
          letterSpacing: '-0.05em',
          background: 'linear-gradient(135deg, var(--accent-teal), var(--accent-purple))',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          marginBottom: '1.5rem',
        }}
      >
        404
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.5 }}
        style={{
          fontSize: 'clamp(1.5rem, 3vw, 2.5rem)',
          fontWeight: 700,
          fontFamily: 'var(--font-heading)',
          color: '#f8fafc',
          marginBottom: '1rem',
          letterSpacing: '-0.02em',
        }}
      >
        {t('notfound.title', 'Página no encontrada')}
      </motion.h1>

      <motion.p
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
        style={{
          color: 'rgba(255,255,255,0.65)',
          maxWidth: '480px',
          margin: '0 auto 2.5rem',
          fontSize: '1.05rem',
          lineHeight: 1.6,
        }}
      >
        {t('notfound.description', 'La página que buscas no existe o fue movida. Vuelve al inicio para encontrar lo que necesitas.')}
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', justifyContent: 'center' }}
      >
        <motion.button
          onClick={() => navigate('/')}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.98 }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '0.9rem 1.8rem',
            borderRadius: 12,
            border: 'none',
            background: 'var(--accent-teal)',
            color: '#00201d',
            fontWeight: 700,
            fontFamily: 'var(--font-heading)',
            fontSize: '0.95rem',
            cursor: 'pointer',
            boxShadow: '0 10px 30px -10px rgba(0,229,209,0.5)',
          }}
        >
          <Home size={17} />
          {t('notfound.home_btn', 'Ir al inicio')}
        </motion.button>

        <motion.button
          onClick={() => navigate(-1)}
          whileHover={{ y: -2 }}
          whileTap={{ scale: 0.98 }}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            padding: '0.9rem 1.8rem',
            borderRadius: 12,
            border: '1px solid rgba(255,255,255,0.16)',
            background: 'rgba(255,255,255,0.06)',
            color: '#f1f5f9',
            fontWeight: 600,
            fontFamily: 'var(--font-heading)',
            fontSize: '0.95rem',
            cursor: 'pointer',
          }}
        >
          <ArrowLeft size={17} />
          {t('notfound.back_btn', 'Volver atrás')}
        </motion.button>
      </motion.div>
      </div>
    </motion.div>
  );
};

export default NotFoundPage;
